import React from "react";

/**
 * Inline styles for plbx pages. Single string to avoid touching the upstream
 * SCSS pipeline. Loaded once from the router shell.
 *
 * Design system tokens are scoped via CSS variables on `.plbx-page` so the
 * dark-mode override is one block at the bottom — no duplication.
 */
const css = `
/* --- boards layout --- */
.plbx-boards-layout {
  /* Hoist tokens so sidebar + modal can access them outside .plbx-page */
  --plbx-bg: #fafafb;
  --plbx-surface: #ffffff;
  --plbx-surface-hover: #f5f6f8;
  --plbx-border: #e5e6ec;
  --plbx-border-strong: #cdcfd6;
  --plbx-text: #14151a;
  --plbx-text-muted: #5f6470;
  --plbx-text-subtle: #8d92a0;
  --plbx-accent: #6965db;
  --plbx-accent-hover: #5854c4;
  --plbx-accent-contrast: #ffffff;
  --plbx-danger: #c62828;
  --plbx-danger-bg: #fdecea;
  --plbx-danger-border: #f0c0c0;
  --plbx-pin: #f0a23a;
  --plbx-shadow-sm: 0 1px 2px rgba(20, 21, 26, 0.04);
  --plbx-shadow-md: 0 4px 16px rgba(20, 21, 26, 0.08);

  display: flex;
  min-height: 100vh;
  background: var(--plbx-bg);
  font-family: 'Cascadia', 'Inter', system-ui, -apple-system, sans-serif;
  font-size: 14px;
  color: var(--plbx-text);
}
.plbx-boards-layout .plbx-page {
  flex: 1;
  min-width: 0;
  min-height: 100vh;
}
.plbx-boards-layout * { box-sizing: border-box; }

@media (prefers-color-scheme: dark) {
  .plbx-boards-layout {
    --plbx-bg: #0f1014;
    --plbx-surface: #1a1b21;
    --plbx-surface-hover: #23242c;
    --plbx-border: #2a2c36;
    --plbx-border-strong: #3a3d4a;
    --plbx-text: #e8e9ee;
    --plbx-text-muted: #a2a6b4;
    --plbx-text-subtle: #6b6f7d;
    --plbx-accent: #8b87f0;
    --plbx-accent-hover: #a5a2f5;
    --plbx-danger: #ff6b6b;
    --plbx-danger-bg: rgba(255, 107, 107, 0.12);
    --plbx-danger-border: rgba(255, 107, 107, 0.35);
    --plbx-pin: #ffc870;
    --plbx-shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.3);
    --plbx-shadow-md: 0 4px 16px rgba(0, 0, 0, 0.45);
  }
}

.plbx-page {
  /* --- tokens (light) --- */
  --plbx-bg: #fafafb;
  --plbx-surface: #ffffff;
  --plbx-surface-hover: #f5f6f8;
  --plbx-border: #e5e6ec;
  --plbx-border-strong: #cdcfd6;
  --plbx-text: #14151a;
  --plbx-text-muted: #5f6470;
  --plbx-text-subtle: #8d92a0;
  --plbx-accent: #6965db;
  --plbx-accent-hover: #5854c4;
  --plbx-accent-contrast: #ffffff;
  --plbx-danger: #c62828;
  --plbx-danger-bg: #fdecea;
  --plbx-danger-border: #f0c0c0;
  --plbx-pin: #f0a23a;
  --plbx-shadow-sm: 0 1px 2px rgba(20, 21, 26, 0.04);
  --plbx-shadow-md: 0 4px 16px rgba(20, 21, 26, 0.08);

  box-sizing: border-box;
  min-height: 100vh;
  padding: 48px 32px 80px;
  background: var(--plbx-bg);
  color: var(--plbx-text);
  font-family: 'Cascadia', 'Inter', system-ui, -apple-system, sans-serif;
  font-size: 14px;
  line-height: 1.5;
}
.plbx-page * { box-sizing: border-box; }
.plbx-page__inner { max-width: 1200px; margin: 0 auto; }

/* --- header --- */
.plbx-page__header {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 32px;
}
.plbx-page__header h1 {
  margin: 0; font-size: 32px; font-weight: 700; letter-spacing: -0.02em;
  color: var(--plbx-text);
}

/* --- toolbar --- */
.plbx-toolbar {
  display: flex; gap: 12px; align-items: center; flex-wrap: wrap;
  margin-bottom: 28px;
}
.plbx-input {
  padding: 10px 14px; border: 1px solid var(--plbx-border);
  border-radius: 10px; font: inherit; font-size: 14px;
  background: var(--plbx-surface); color: var(--plbx-text);
  min-width: 280px; transition: border-color 120ms, box-shadow 120ms;
}
.plbx-input::placeholder { color: var(--plbx-text-subtle); }
.plbx-input:focus {
  outline: none; border-color: var(--plbx-accent);
  box-shadow: 0 0 0 3px rgba(105, 101, 219, 0.18);
}
.plbx-checkbox {
  display: inline-flex; gap: 8px; align-items: center;
  font-size: 14px; color: var(--plbx-text-muted); cursor: pointer;
  padding: 8px 10px; border-radius: 8px;
}
.plbx-checkbox:hover { background: var(--plbx-surface-hover); }
.plbx-checkbox input { accent-color: var(--plbx-accent); cursor: pointer; }

/* --- buttons --- */
.plbx-btn {
  padding: 9px 14px; border: 1px solid var(--plbx-border-strong);
  border-radius: 10px; background: var(--plbx-surface);
  color: var(--plbx-text); cursor: pointer; font: inherit;
  font-size: 14px; font-weight: 500; line-height: 1;
  transition: background 120ms, border-color 120ms, transform 80ms;
}
.plbx-btn:hover { background: var(--plbx-surface-hover); }
.plbx-btn:active { transform: translateY(1px); }
.plbx-btn--primary {
  background: var(--plbx-accent); border-color: var(--plbx-accent);
  color: var(--plbx-accent-contrast);
  box-shadow: var(--plbx-shadow-sm);
}
.plbx-btn--primary:hover {
  background: var(--plbx-accent-hover); border-color: var(--plbx-accent-hover);
}
.plbx-btn--ghost {
  background: transparent; border-color: transparent;
  color: var(--plbx-text-muted);
}
.plbx-btn--ghost:hover { background: var(--plbx-surface-hover); color: var(--plbx-text); }
.plbx-btn--danger {
  color: var(--plbx-danger); border-color: var(--plbx-danger-border);
  background: transparent;
}
.plbx-btn--danger:hover { background: var(--plbx-danger-bg); }
.plbx-btn:disabled { opacity: 0.55; cursor: not-allowed; }

/* --- states --- */
.plbx-empty {
  padding: 64px 32px; text-align: center; color: var(--plbx-text-subtle);
  border: 1px dashed var(--plbx-border); border-radius: 12px;
  background: var(--plbx-surface);
}
.plbx-alert {
  padding: 12px 16px; border-radius: 10px; margin-bottom: 16px;
  font-size: 14px;
}
.plbx-alert--error {
  background: var(--plbx-danger-bg); color: var(--plbx-danger);
  border: 1px solid var(--plbx-danger-border);
}

/* --- board grid --- */
.plbx-board-list {
  list-style: none; padding: 0; margin: 0;
  display: grid; gap: 16px;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
}
.plbx-board-card {
  display: flex; flex-direction: column; gap: 12px;
  padding: 18px 18px 14px; border: 1px solid var(--plbx-border);
  border-radius: 14px; background: var(--plbx-surface);
  box-shadow: var(--plbx-shadow-sm);
  transition: border-color 120ms, box-shadow 120ms, transform 120ms;
  position: relative;
}
.plbx-board-card:hover {
  border-color: var(--plbx-border-strong);
  box-shadow: var(--plbx-shadow-md);
  transform: translateY(-1px);
}
.plbx-board-card__open {
  display: flex; flex-direction: column; gap: 6px;
  background: transparent; border: none; cursor: pointer;
  text-align: left; padding: 0; color: inherit; font: inherit;
}
.plbx-board-card__name {
  font-size: 16px; font-weight: 600; color: var(--plbx-text);
  line-height: 1.35; word-break: break-word;
  display: flex; gap: 8px; align-items: flex-start;
}
.plbx-board-card__pin {
  flex-shrink: 0; display: inline-flex; align-items: center;
  justify-content: center; width: 18px; height: 18px;
  border-radius: 5px; background: rgba(240, 162, 58, 0.16);
  color: var(--plbx-pin); font-size: 11px; line-height: 1;
}
.plbx-board-card__meta {
  font-size: 12px; color: var(--plbx-text-subtle);
}
.plbx-board-card__edit {
  display: flex; flex-direction: column; gap: 6px;
}
.plbx-board-card__edit .plbx-input {
  min-width: 0; width: 100%; padding: 8px 10px; font-size: 15px;
  font-weight: 600;
}
.plbx-board-card__actions {
  display: flex; flex-wrap: wrap; gap: 4px; margin-top: auto;
  padding-top: 10px; border-top: 1px solid var(--plbx-border);
}
.plbx-board-card__actions .plbx-btn {
  padding: 5px 9px; font-size: 12px; font-weight: 500;
}

/* --- tables / api-keys page --- */
.plbx-table { width: 100%; border-collapse: collapse; background: var(--plbx-surface);
  border: 1px solid var(--plbx-border); border-radius: 12px; overflow: hidden; }
.plbx-table th, .plbx-table td {
  padding: 12px 16px; border-bottom: 1px solid var(--plbx-border);
  text-align: left; font-size: 14px;
}
.plbx-table tr:last-child td { border-bottom: none; }
.plbx-table th { font-weight: 600; color: var(--plbx-text-muted);
  background: var(--plbx-surface-hover); font-size: 12px;
  text-transform: uppercase; letter-spacing: 0.04em; }

/* --- modal --- */
.plbx-modal-backdrop {
  position: fixed; inset: 0; background: rgba(20, 21, 26, 0.55);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999; padding: 24px;
}
.plbx-modal {
  background: var(--plbx-surface); border-radius: 14px; padding: 24px 28px;
  min-width: 420px; max-width: 600px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  color: var(--plbx-text);
}
.plbx-modal h2 { margin: 0 0 12px; font-size: 20px; font-weight: 600; }
.plbx-modal__list {
  list-style: none; padding: 0; margin: 0 0 16px;
  display: flex; flex-direction: column; gap: 4px;
}
.plbx-modal__list .plbx-btn {
  width: 100%; text-align: left;
}
.plbx-modal__actions {
  display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px;
}
.plbx-field { display: flex; flex-direction: column; gap: 6px; font-size: 14px; }
.plbx-token-row {
  display: flex; gap: 8px; align-items: center;
  padding: 12px 14px; background: var(--plbx-surface-hover);
  border: 1px solid var(--plbx-border); border-radius: 10px;
  font-family: ui-monospace, 'SF Mono', monospace;
}
.plbx-token { flex: 1; word-break: break-all; font-size: 13px;
  color: var(--plbx-text); }

/* --- workspace switcher --- */
.plbx-ws-switcher {
  width: 240px;
  flex-shrink: 0;
  padding: 24px 16px;
  background: var(--plbx-surface);
  border-right: 1px solid var(--plbx-border);
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
}
.plbx-ws-switcher__header {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: var(--plbx-text-subtle);
  margin-bottom: 8px;
  padding: 0 8px;
}
.plbx-ws-switcher__list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.plbx-ws-switcher__sep {
  border: none;
  border-top: 1px solid var(--plbx-border);
  margin: 12px 0;
}
.plbx-ws-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  color: var(--plbx-text);
  user-select: none;
}
.plbx-ws-row:hover {
  background: var(--plbx-surface-hover);
}
.plbx-ws-row--active {
  background: var(--plbx-accent);
  color: var(--plbx-accent-contrast);
}
.plbx-ws-row--active:hover {
  background: var(--plbx-accent-hover);
}
.plbx-ws-row__name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.plbx-ws-row__count {
  font-size: 11px;
  color: inherit;
  opacity: 0.7;
}
.plbx-ws-switcher__footer {
  margin-top: auto;
  padding-top: 16px;
  font-size: 11px;
  color: var(--plbx-text-subtle);
}
.plbx-ws-switcher__footer a {
  color: var(--plbx-text-subtle);
  text-decoration: none;
}
.plbx-ws-switcher__footer a:hover {
  text-decoration: underline;
}
.plbx-ws-switcher--error {
  color: var(--plbx-danger);
  padding: 12px;
  font-size: 12px;
}

/* --- floating board title chip (rendered on canvas) --- */
.plbx-board-title {
  position: fixed; top: 14px; left: 64px; z-index: 9;
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 12px; border-radius: 10px;
  background: var(--plbx-surface, #fff);
  border: 1px solid var(--plbx-border, #e5e6ec);
  box-shadow: var(--plbx-shadow-sm, 0 1px 2px rgba(0,0,0,0.06));
  color: var(--plbx-text, #14151a);
  font-family: 'Cascadia', 'Inter', system-ui, sans-serif;
  font-size: 14px; font-weight: 500; line-height: 1.2;
  max-width: 50vw; cursor: pointer;
  transition: background 120ms, transform 80ms;
}
.plbx-board-title[role="button"]:hover { background: var(--plbx-surface-hover, #f5f6f8); }
.plbx-board-title[role="button"]:active { transform: translateY(1px); }
.plbx-board-title__text {
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  max-width: 44vw;
}
.plbx-board-title__badge {
  display: inline-flex; align-items: center; padding: 2px 6px;
  font-size: 11px; font-weight: 600; letter-spacing: 0.04em;
  text-transform: uppercase;
  background: rgba(105, 101, 219, 0.14); color: #6965db;
  border-radius: 6px;
}
.plbx-board-title__status {
  font-size: 12px; font-weight: 500; line-height: 1.2;
  white-space: nowrap; opacity: 0.9;
  transition: color 120ms, opacity 120ms;
}
.plbx-board-title__status--saving { color: var(--plbx-text-subtle, #8a8d99); }
.plbx-board-title__status--saved  { color: #2f9e44; }
.plbx-board-title__status--error  {
  color: var(--plbx-danger, #e03131); font-weight: 600;
}
@media (prefers-color-scheme: dark) {
  .plbx-board-title {
    background: #1a1b21; border-color: #2a2c36; color: #e8e9ee;
    box-shadow: 0 1px 2px rgba(0,0,0,0.4);
  }
  .plbx-board-title[role="button"]:hover { background: #23242c; }
  .plbx-board-title__badge {
    background: rgba(139, 135, 240, 0.20); color: #b1aef5;
  }
  .plbx-board-title__status--saved { color: #51cf66; }
}

/* --- workspace badge + lock icon --- */
.plbx-ws-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #fff;
  text-transform: lowercase;
  margin-right: 8px;
  vertical-align: middle;
}
.plbx-board-card__lock {
  display: inline-block;
  margin-right: 6px;
  font-size: 12px;
  opacity: 0.7;
}
.plbx-board-card__meta {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

/* --- editor breadcrumb --- */
.plbx-board-title__crumb {
  color: var(--plbx-text-muted);
  text-decoration: none;
  font-weight: 500;
  margin-right: 6px;
}
.plbx-board-title__crumb:hover {
  color: var(--plbx-accent);
  text-decoration: underline;
}
.plbx-board-title__sep {
  color: var(--plbx-text-subtle);
  margin: 0 4px;
}
.plbx-board-title__text {
  cursor: pointer;
}

/* --- api key scope selector --- */
.plbx-scope-radio {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.plbx-scope-radio .plbx-radio {
  display: flex;
  align-items: center;
  gap: 8px;
}
.plbx-radio {
  font-size: 13px;
  color: var(--plbx-text);
}

/* Excalidraw in-app dark theme — overrides the system preference. The chip
   lives outside the Excalidraw root, so we reach across via :has() on body. */
body:has(.excalidraw.theme--dark) .plbx-board-title {
  background: #1a1b21;
  border-color: #2a2c36;
  color: #e8e9ee;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
}
body:has(.excalidraw.theme--dark) .plbx-board-title[role="button"]:hover {
  background: #23242c;
}
body:has(.excalidraw.theme--dark) .plbx-board-title__badge {
  background: rgba(139, 135, 240, 0.20);
  color: #b1aef5;
}
body:has(.excalidraw.theme--dark) .plbx-board-title__crumb {
  color: #a2a6b4;
}
body:has(.excalidraw.theme--dark) .plbx-board-title__crumb:hover {
  color: #b1aef5;
}
body:has(.excalidraw.theme--dark) .plbx-board-title__sep {
  color: #6b6f7d;
}
body:has(.excalidraw.theme--dark) .plbx-board-title__status--saved {
  color: #51cf66;
}

/* --- dark mode --- */
@media (prefers-color-scheme: dark) {
  .plbx-page {
    --plbx-bg: #0f1014;
    --plbx-surface: #1a1b21;
    --plbx-surface-hover: #23242c;
    --plbx-border: #2a2c36;
    --plbx-border-strong: #3a3d4a;
    --plbx-text: #e8e9ee;
    --plbx-text-muted: #a2a6b4;
    --plbx-text-subtle: #6b6f7d;
    --plbx-accent: #8b87f0;
    --plbx-accent-hover: #a5a2f5;
    --plbx-danger: #ff6b6b;
    --plbx-danger-bg: rgba(255, 107, 107, 0.12);
    --plbx-danger-border: rgba(255, 107, 107, 0.35);
    --plbx-pin: #ffc870;
    --plbx-shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.3);
    --plbx-shadow-md: 0 4px 16px rgba(0, 0, 0, 0.45);
  }
}
`;

export const PlbxStyles: React.FC = () => <style>{css}</style>;
export default PlbxStyles;
